import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const milestones = [
  { year: '早年', icon: 'ri-home-heart-line', title: '出身乡村，刻苦求学', desc: '创始人自幼出身农村，家境清贫，却从未放弃对知识的渴望，立志走出大山，用知识改变命运。' },
  { year: '求学', icon: 'ri-book-open-line', title: '名牌大学博士学位', desc: '凭借坚韧不拔的毅力，他最终考入名牌大学，并以优异成绩获得博士学位，完成了从乡村到学术殿堂的跨越。' },
  { year: '创业', icon: 'ri-building-2-line', title: '创办多家杰出企业', desc: '学成之后投身商界，凭借卓越的眼光与魄力，成功创办了多家杰出企业，成为业界知名的超级富豪。' },
  { year: '为父', icon: 'ri-parent-line', title: '三个孩子，城市长大', desc: '如今他育有三个子女，孩子们在城市中衣食无忧，平时也会花些时间玩游戏，缺少对奋斗与感恩的深刻体会。' },
  { year: '初心', icon: 'ri-heart-line', title: '城乡孩子互相学习', desc: '他希望城市的孩子结识乡村朋友，学会珍惜与感恩；乡村的孩子则能从城市同伴那里学会流利的公众表达，双方取长补短。' },
  { year: '2022', icon: 'ri-rocket-line', title: '创办金榜旺财', desc: '带着这份深沉的父爱与社会责任，他倾注心血创办了这一卓越平台，让城乡孩子相互学习、共同进步，打破教育壁垒。' },
];

const values = [
  { icon: 'ri-seedling-line', title: '教育公平', desc: '城乡学生共享优质教育资源，打破地域与阶层限制，让每一个孩子站在同一起跑线上。', color: 'bg-green-50 text-green-600' },
  { icon: 'ri-heart-line', title: '感恩成长', desc: '城市孩子从乡村同伴身上学会感恩与珍惜，乡村孩子从城市同伴那里获得更广阔的视野与表达能力。', color: 'bg-pink-50 text-pink-600' },
  { icon: 'ri-team-line', title: '互助共进', desc: '学生之间跨越地域相互帮助，城乡取长补短，共同进步，共同成长，共同金榜题名。', color: 'bg-amber-50 text-amber-600' },
  { icon: 'ri-trophy-line', title: '追求卓越', desc: '以高考考研为核心，同时覆盖CPA、护士执照、四六级、执业医师、法律资格等职业考试，助力每位学生实现梦想。', color: 'bg-red-50 text-red-600' },
];

const team = [
  { name: '平台创始人', role: '出身乡村的超级富豪 · 名牌大学博士', avatar: 'https://public.readdy.ai/ai/img_res/91a445682bb1385ed846f2796e4cd185.jpg' },
  { name: '林晓燕', role: '联合创始人 & 教研总监', avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20female%20education%20director%20portrait%2C%20professional%2C%20warm%20smile%2C%20clean%20background&width=120&height=120&seq=team002&orientation=squarish' },
  { name: '张国强', role: '技术总监 CTO', avatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20tech%20director%20portrait%2C%20professional%2C%20glasses%2C%20clean%20background&width=120&height=120&seq=team003&orientation=squarish' },
  { name: '王美华', role: '运营总监 COO', avatar: 'https://readdy.ai/api/search-image?query=Chinese%20female%20operations%20director%20portrait%2C%20professional%2C%20confident%2C%20clean%20background&width=120&height=120&seq=team004&orientation=squarish' },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${SITE_URL}/about`,
  url: `${SITE_URL}/about`,
  name: '关于金榜旺财 - 城乡教育公益平台创始故事',
  description: '金榜旺财由一位出身乡村的超级富豪父亲创办，致力于打破城乡教育壁垒，让城市孩子学会感恩，让乡村孩子走向世界，助力每一个孩子金榜题名。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_URL}/about` },
    ],
  },
};

export default function AboutPage() {
  const [activeIdx, setActiveIdx] = useState(5);

  useEffect(() => {
    const id = 'schema-about';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(aboutSchema);
    document.title = '关于金榜旺财 - 城乡教育公益平台创始故事';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '金榜旺财由一位出身乡村的超级富豪父亲创办，致力于打破城乡教育壁垒，让城市孩子学会感恩，让乡村孩子走向世界，助力每一个孩子金榜题名。');
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />

      {/* Hero Header — pink-green theme matching homepage */}
      <div className="relative pt-20 pb-20 bg-gradient-to-br from-pink-900 via-green-900 to-pink-800 overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-pink-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-green-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-seedling-line text-amber-400 text-sm"></i>
            <span className="text-amber-300 text-sm font-medium">一位父亲的心愿 · 千万孩子的未来</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4">关于金榜旺财</h1>
          <p className="text-white/70 text-base max-w-xl mx-auto leading-relaxed">
            打破城乡教育壁垒，让城市孩子学会感恩，让乡村孩子走向世界，让每一个孩子都能金榜题名。
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-16">

        {/* Founder Story */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-quill-pen-line text-amber-600 text-sm"></i>
              <span className="text-amber-700 text-xs font-semibold">创办初心</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-3">
              一位超级富豪父亲的心愿
            </h2>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
              打造这一卓越平台的初衷，源于一位出身乡村的超级富豪父亲。他自幼刻苦求学，最终获得了名牌大学的博士学位，并成功创办了多家杰出企业。如今，这位富豪育有三个子女，孩子们平时也会花些时间玩游戏。为了引导这些在城市长大的孩子懂得珍惜学习时光、培养更多积极向上的精神，并像乡村孩子那样学会感恩父母，他们需要结识一些来自乡村的朋友。这样一来，城市的孩子们便能从乡村同伴身上汲取养分、相互学习；与此同时，乡村的孩子们也能从城市同伴那里学会如何流利地进行公众表达，双方从而能够取长补短、共同进步。
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Founder photo + quote */}
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=successful%20middle-aged%20Chinese%20businessman%20entrepreneur%2C%20warm%20fatherly%20smile%2C%20wearing%20casual%20suit%2C%20standing%20in%20a%20bright%20modern%20office%20with%20bookshelves%2C%20confident%20and%20kind%20expression%2C%20professional%20portrait%20photography%2C%20natural%20lighting&width=600&height=500&seq=founder_about01&orientation=landscape"
                alt="平台创始人"
                className="w-full h-80 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="text-3xl text-amber-400/50 font-serif leading-none mb-2">&ldquo;</div>
                <p className="text-white text-sm leading-relaxed">
                  城市的孩子们能从乡村同伴身上汲取养分、学会感恩；乡村的孩子们能从城市同伴那里学会流利的公众表达——双方取长补短，共同进步。这就是我创办这个平台的初衷。
                </p>
                <div className="mt-3">
                  <p className="text-amber-300 font-bold text-sm">平台创始人</p>
                  <p className="text-white/60 text-xs">出身乡村的超级富豪 · 名牌大学博士 · 三个孩子的父亲</p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h3 className="text-base font-black text-gray-900 mb-4">创始人历程</h3>
              <div className="flex flex-col gap-2">
                {milestones.map((m, idx) => (
                  <button
                    key={m.year}
                    onClick={() => setActiveIdx(idx)}
                    className={`w-full text-left rounded-2xl p-4 transition-all duration-300 cursor-pointer ${
                      activeIdx === idx ? 'bg-gray-900 text-white' : 'bg-white hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${activeIdx === idx ? 'bg-amber-500' : 'bg-gray-100'}`}>
                        <i className={`${m.icon} text-sm ${activeIdx === idx ? 'text-white' : 'text-gray-500'}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs font-bold ${activeIdx === idx ? 'text-amber-400' : 'text-gray-400'}`}>{m.year}</span>
                          <span className={`font-bold text-sm ${activeIdx === idx ? 'text-white' : 'text-gray-800'}`}>{m.title}</span>
                        </div>
                        {activeIdx === idx && <p className="text-white/75 text-xs leading-relaxed mt-1">{m.desc}</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Platform Mission */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-3">平台使命与价值观</h2>
            <p className="text-gray-500 text-sm">我们相信，每一个孩子都值得拥有改变命运的机会</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-6 text-center hover:scale-[1.02] transition-all">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-4 ${v.color}`}>
                  <i className={`${v.icon} text-2xl`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{v.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-br from-pink-900 via-green-900 to-pink-800 rounded-3xl p-10 mb-20 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-pink-400/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-green-400/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="text-center mb-8">
            <h2 className="text-white font-black text-2xl mb-1">平台成就</h2>
            <p className="text-white/60 text-sm">截至2024年，我们已帮助数十万学生改变命运</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
            {[
              { num: '50万+', label: '注册学生', icon: 'ri-user-smile-line' },
              { num: '2万+', label: '教学视频', icon: 'ri-video-line' },
              { num: '8000+', label: '认证教师', icon: 'ri-user-star-line' },
              { num: '98%', label: '升学率', icon: 'ri-trophy-line' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-white/15 rounded-xl mx-auto mb-3">
                  <i className={`${s.icon} text-amber-300 text-lg`}></i>
                </div>
                <div className="text-3xl font-black text-amber-400 mb-1">{s.num}</div>
                <div className="text-white/70 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-3">核心团队</h2>
            <p className="text-gray-500 text-sm">一群有梦想、有情怀的教育人，共同守护每个孩子的未来</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {team.map((member) => (
              <div key={member.name} className="bg-white rounded-2xl p-6 text-center hover:scale-[1.02] transition-all">
                <img src={member.avatar} alt={member.name} className="w-16 h-16 rounded-full object-cover object-top mx-auto mb-3 border-2 border-pink-100" />
                <p className="font-bold text-gray-900 text-sm">{member.name}</p>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed">{member.role}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-white rounded-3xl p-10">
          <h2 className="text-2xl font-black text-gray-900 mb-3">加入我们，共同成长</h2>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto leading-relaxed">
            无论你来自城市还是乡村，这里都有你的位置。城乡互助，共同成长，让每一个努力的孩子都能金榜题名。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/videos" className="px-7 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-play-circle-line"></i>开始学习
            </Link>
            <Link to="/community" className="px-7 py-3 border-2 border-gray-200 hover:border-pink-300 text-gray-700 hover:text-pink-600 font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-team-line"></i>加入社区
            </Link>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
}
